import { useEffect, useRef } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

/**
 * The reCAPTCHA header, for the payment endpoints the new billing flow
 * deprecates: stripe/createSubscription, stripe/activateSubscription,
 * addPaymentMethodToOrganization and updateSubscriptionPlan. They are rate
 * limited, and a request to them without the header is refused — on staging
 * with a database error ("String or binary data would be truncated in table
 * ...Logs, column 'TraceId'"), which is the backend failing to log the missing
 * token.
 *
 * Returns a function that resolves to the headers for one request — a token is
 * good for a single use, so each call gets its own. A call made before the
 * script has loaded waits for it. When no token can be had (the script was
 * blocked, never arrived, or refused) the function rejects with an error whose
 * message says so, and nothing is sent: a request without the header is only
 * ever refused, with a message nobody can act on.
 *
 * The action is "login" because that is the action every other call in the app
 * is verified under.
 */
export function useRecaptchaHeaders() {
   const { executeRecaptcha } = useGoogleReCaptcha();
   const recaptcha = useRef(null);

   if (!recaptcha.current) recaptcha.current = createRecaptchaHeaders();

   useEffect(() => {
      recaptcha.current.setExecute(executeRecaptcha);
   }, [executeRecaptcha]);

   return recaptcha.current.headers;
}

export const RECAPTCHA_UNAVAILABLE = "RECAPTCHA_UNAVAILABLE";

export const RECAPTCHA_UNAVAILABLE_MESSAGE =
   "The security check didn't load, so nothing was sent. Please check your connection and " +
   "any ad blocker, reload the page and try again.";

// The script is loaded as the app starts, so this only runs out when it was
// blocked or never arrived.
const LOAD_TIMEOUT_MS = 10000;

// A code rather than a subclass of Error: `instanceof` on a transpiled subclass
// of a built-in is not to be relied on.
const unavailable = (cause) => {
   const error = new Error(RECAPTCHA_UNAVAILABLE_MESSAGE);
   error.code = RECAPTCHA_UNAVAILABLE;
   error.cause = cause;
   return error;
};

export const isRecaptchaUnavailable = (error) => error?.code === RECAPTCHA_UNAVAILABLE;

/**
 * The part of the hook that doesn't need React. `setExecute` takes the
 * provider's executeRecaptcha, which is undefined until the script has loaded;
 * `headers` is the function the hook hands out.
 */
export function createRecaptchaHeaders({ timeoutMs = LOAD_TIMEOUT_MS } = {}) {
   let execute = null;
   let waiting = [];

   const whenLoaded = () => {
      if (execute) return Promise.resolve(execute);

      return new Promise((resolve) => {
         const settle = (loaded) => {
            clearTimeout(timer);
            resolve(loaded);
         };
         const timer = setTimeout(() => {
            waiting = waiting.filter((waiter) => waiter !== settle);
            resolve(null);
         }, timeoutMs);

         waiting.push(settle);
      });
   };

   return {
      setExecute(next) {
         execute = next ?? null;
         if (!execute) return;

         const ready = waiting;
         waiting = [];
         ready.forEach((settle) => settle(execute));
      },

      async headers() {
         const loaded = await whenLoaded();
         if (!loaded) throw unavailable();

         let token;
         try {
            token = await loaded("login");
         } catch (refused) {
            throw unavailable(refused);
         }

         if (!token) throw unavailable();
         return { "g-recaptcha-response": token };
      }
   };
}
